import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { FlowTracker } from '@/components/analytics/FlowTracker';
import { tagB44ClickId } from '@/components/checkoutRedirect';
import { HENVISNING_UTM_SOURCE, isValidKNumber } from '@/components/henvisning/HenvisningConstants';

/**
 * ReferralTouchTracker
 *
 * When a visitor lands with a henvisning referral link
 * (utm_source=henvisning & utm_medium=K-number), we:
 *  1. Persist a durable ReferralTouch keyed on the persistent visitor_id
 *     (flow_user_id, survives across sessions on the same device/browser).
 *  2. Tag the b44_click_id with _hv_<visitorId> so the persistent ID survives
 *     the external checkout round-trip and comes back in the SaleEvent.
 *
 * This enables attributing referrals even when the final (last) click comes
 * back direct or via another channel — as long as it's the same device/browser.
 */
export default function ReferralTouchTracker() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = (params.get('utm_source') || '').toLowerCase();
    const kNumber = (params.get('utm_medium') || '').trim().toUpperCase();

    // Only act on genuine henvisning referral links with a valid K-number
    if (utmSource !== HENVISNING_UTM_SOURCE || !kNumber || !isValidKNumber(kNumber)) return;

    const visitorId = FlowTracker.getUserId();
    if (!visitorId) return;

    // Tag the b44 click id so the persistent visitor ID survives external checkout.
    // The suffix is safe — it sits alongside existing _pa_/_rp_/_mi_ suffixes.
    tagB44ClickId(`_hv_${visitorId}`);

    const now = new Date().toISOString();
    let referrerDomain = '';
    try { referrerDomain = new URL(document.referrer).hostname.replace('www.', ''); } catch {}

    (async () => {
      try {
        // Upsert: one touch row per (referrer, visitor) pair
        const existing = await base44.entities.ReferralTouch.filter({
          referrer_customer_number: kNumber,
          visitor_id: visitorId,
        });

        if (existing && existing.length > 0) {
          const touch = existing[0];
          await base44.entities.ReferralTouch.update(touch.id, {
            last_touch_date: now,
            touch_count: (touch.touch_count || 1) + 1,
            session_id: FlowTracker.getSessionId(),
          });
        } else {
          await base44.entities.ReferralTouch.create({
            referrer_customer_number: kNumber,
            visitor_id: visitorId,
            session_id: FlowTracker.getSessionId(),
            b44_click_id: FlowTracker.getB44ClickId(),
            first_touch_date: now,
            last_touch_date: now,
            touch_count: 1,
            landing_url: window.location.href,
            landing_page: window.location.pathname,
            referrer_domain: referrerDomain,
            device_type: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            matched: false,
          });
        }
      } catch {
        // non-blocking — tracking should never break the page
      }
    })();
  }, [location.pathname, location.search]);

  return null;
}